import { generalConditionsAcceptedGuard } from '@/features/general-conditions/general-conditions-accepted.guard';
import { passwordExpiredChangedGuard } from '@/features/password-expired/password-expired-changed.guard';
import { isAuthenticatedGuard } from '@/services/routing/is-authenticated.guard';
import { isNotAuthenticatedGuard } from '@/services/routing/is-not-authenticated.guard';
import { NgModule } from '@angular/core';
import { RouterModule, type ExtraOptions } from '@angular/router';
import { AppShellComponent } from '../components/app-shell/app-shell';

const routerOptions: ExtraOptions = {
    bindToComponentInputs: true,
    paramsInheritanceStrategy: 'always'
};
@NgModule({
    imports: [
        RouterModule.forRoot(
            [
                {
                    path: '',
                    component: AppShellComponent,
                    canActivate: [isNotAuthenticatedGuard],
                    children: [
                        // Routes with noAuthGuard
                        {
                            path: '',
                            redirectTo: 'login',
                            pathMatch: 'full'
                        },
                        {
                            path: 'login',
                            loadChildren: () => import('@/features/auth/login.routes').then(m => m.loginRoutes),
                            data: {
                                standalone: true,
                                team: 'structure'
                            }
                        },
                        {
                            path: 'account-creation',
                            loadChildren: () => import('@/features/auth/account-creation.routes').then(m => m.accountCreationRoutes),
                            data: {
                                standalone: true,
                                team: 'structure'
                            }
                        }
                    ]
                },
                {
                    path: '',
                    canActivate: [isAuthenticatedGuard],
                    children: [
                        {
                            path: '',
                            component: AppShellComponent,
                            children: [
                                // Routes with only authGuard
                                {
                                    path: '',
                                    redirectTo: 'general-conditions',
                                    pathMatch: 'full'
                                },
                                {
                                    path: 'general-conditions',
                                    loadChildren: () =>
                                        import('@/features/general-conditions/general-conditions.routes').then(m => m.routes),
                                    data: {
                                        standalone: true,
                                        team: 'structure'
                                    }
                                },
                                {
                                    path: 'password-expired',
                                    loadChildren: () => import('@/features/password-expired/password-expired.routes').then(m => m.routes),
                                    data: {
                                        standalone: true,
                                        team: 'structure'
                                    }
                                }
                            ]
                        },
                        {
                            path: '',
                            component: AppShellComponent,
                            canActivate: [generalConditionsAcceptedGuard, passwordExpiredChangedGuard],
                            children: [
                                // Routes with authGuard, generalConditionsAcceptedGuard and passwordExpiredChangedGuard
                                {
                                    path: '',
                                    redirectTo: 'homepage',
                                    pathMatch: 'full'
                                },
                                {
                                    path: 'alerts',
                                    data: {
                                        team: 'ems'
                                    },
                                    loadChildren: () => import('@/features/alerts/alerts.routes').then(m => m.routes)
                                },
                                {
                                    path: 'databoards',
                                    data: {
                                        team: 'ems'
                                    },
                                    loadChildren: () => import('@/features/databoards/databoards.routes').then(m => m.routes)
                                },
                                {
                                    path: 'baseload-analysis',
                                    data: {
                                        standalone: true,
                                        team: 'ems'
                                    },
                                    loadChildren: () => import('@/features/baseload-analysis/baseload-analysis.routes').then(m => m.routes)
                                },
                                {
                                    path: 'custom-business-intelligence-analysis',
                                    data: {
                                        standalone: true,
                                        team: 'ems'
                                    },
                                    loadChildren: () =>
                                        import('@/features/custom-business-intelligence-analysis/custom-business-intelligence-analysis.routes').then(
                                            m => m.routes
                                        )
                                },
                                {
                                    path: 'exports',
                                    data: {
                                        team: 'ems' // to discuss
                                    },
                                    loadChildren: () => import('@/features/exports/exports.routes').then(m => m.routes)
                                },
                                {
                                    path: 'buildings',
                                    data: {
                                        team: 'structure'
                                    },
                                    loadChildren: () =>
                                        import('@/features/building-visualization/building-visualization.routes').then(m => m.routes)
                                },
                                {
                                    path: 'me',
                                    data: {
                                        team: 'structure'
                                    },
                                    loadChildren: () => import('@/features/current-user/current-user.routes').then(m => m.routes)
                                },
                                {
                                    path: 'meters',
                                    data: {
                                        team: 'structure'
                                    },
                                    loadChildren: () => import('@/features/meters/meters.routes').then(m => m.routes)
                                },
                                {
                                    path: 'homepage',
                                    data: {
                                        team: 'structure' // to discuss
                                    },
                                    loadChildren: () => import('@/features/homepage/homepage.routes').then(m => m.routes)
                                },
                                {
                                    path: 'business-indicators',
                                    data: {
                                        team: 'ems'
                                    },
                                    loadChildren: () =>
                                        import('@/features/business-indicators/business-indicators.routes').then(m => m.routes)
                                },
                                {
                                    path: 'admin/business-indicators',
                                    data: {
                                        team: 'ems'
                                    },
                                    loadChildren: () =>
                                        import('@/features/business-indicators/admin-business-indicators.routes').then(m => m.routes)
                                },
                                {
                                    path: 'sen-analysis',
                                    data: {
                                        team: 'ems'
                                    },
                                    loadChildren: () => import('@/features/sen-analysis/sen-analysis.routes').then(m => m.routes)
                                },
                                {
                                    path: 'actions',
                                    data: {
                                        reuse: true,
                                        team: 'ems'
                                    },
                                    loadChildren: () => import('@/features/action-plan/action-plan.routes').then(m => m.routes)
                                },
                                {
                                    path: 'reports',
                                    data: {
                                        reuse: true,
                                        team: 'ems'
                                    },
                                    loadChildren: () => import('@/features/reports/reports.routes').then(m => m.routes)
                                },
                                {
                                    path: 'recommended-actions',
                                    data: {
                                        reuse: true,
                                        team: 'ems'
                                    },
                                    loadChildren: () =>
                                        import('@/features/recommended-actions/recommended-actions.routes').then(m => m.routes)
                                },
                                // old pages to migrate as features
                                {
                                    path: 'delivery-points',
                                    data: {
                                        reuse: true,
                                        team: 'structure'
                                    },
                                    loadComponent: () =>
                                        import('../pages/entity-visualization-page/delivery-points/delivery-points').then(
                                            m => m.DeliveryPointsPage
                                        )
                                },
                                {
                                    path: 'bills/delivery-points/:deliveryPointId',
                                    data: {
                                        team: 'ems' // to discuss
                                    },
                                    loadComponent: () =>
                                        import('../pages/bills/bills-by-delivery-point/bills-by-delivery-point.ng-page').then(
                                            m => m.BillsByDeliveryPointNgPage
                                        )
                                },
                                {
                                    path: 'readings/:buildingUid/:systemUid/edit',
                                    loadComponent: () =>
                                        import('../pages/systems/system-edition/system-edition.ng-page').then(m => m.SystemEditionNgPage),
                                    data: {
                                        team: 'integration'
                                    }
                                },
                                {
                                    path: 'manual-readings/:buildingId',
                                    data: {
                                        team: 'integration',
                                        reuse: true
                                    },
                                    loadComponent: () =>
                                        import('../pages/meter/meters-list/meters-list.ng-page').then(m => m.MetersListNgPage)
                                },
                                {
                                    path: 'maps',
                                    data: {
                                        team: 'structure'
                                    },
                                    loadComponent: () => import('../pages/maps/maps').then(m => m.MapsPage)
                                },
                                {
                                    path: 'admin-users-profiles',
                                    loadComponent: () =>
                                        import('../pages/admin-users-profiles/admin-users-profiles.page').then(
                                            m => m.AdminUsersProfilesPage
                                        ),
                                    data: {
                                        team: 'structure'
                                    }
                                },
                                {
                                    path: 'admin-import-history',
                                    loadComponent: () =>
                                        import('../pages/import-history/admin-import-history.ng-page').then(
                                            m => m.AdminImportHistoryNgPage
                                        ),
                                    data: {
                                        team: 'structure'
                                    }
                                },
                                {
                                    path: 'admin-settings',
                                    loadComponent: () =>
                                        import('../pages/admin-settings/admin-settings.ng-page').then(m => m.AdminSettingsNgPage),
                                    data: {
                                        team: 'structure'
                                    }
                                },
                                {
                                    path: 'attribute-management',
                                    loadComponent: () =>
                                        import('../pages/attribute-management/attribute-management.ng-page').then(
                                            m => m.AttributeManagementNgPage
                                        ),
                                    data: {
                                        team: 'structure'
                                    }
                                },
                                {
                                    path: 'admin-ipmvp',
                                    data: {
                                        team: 'integration' // to discuss
                                    },
                                    loadChildren: () => import('@/features/ipmvp/ipmvp.routes').then(m => m.routes)
                                },
                                {
                                    path: 'iot-supervision',
                                    data: {
                                        team: 'integration'
                                    },
                                    loadChildren: () => import('@/features/iot-supervision/iot-supervision.routes').then(m => m.routes)
                                },
                                {
                                    path: 'classifications',
                                    loadComponent: () =>
                                        import('../pages/classifications/classifications.ng-page').then(m => m.ClassificationsNgPage),
                                    data: {
                                        team: 'structure'
                                    }
                                },
                                {
                                    path: 'dashaviz',
                                    data: {
                                        team: 'ems'
                                    },
                                    loadComponent: () => import('../pages/dashaviz/dashaviz.ng-page').then(m => m.DashavizNgPage)
                                },
                                {
                                    path: 'document-management-system-import',
                                    data: {
                                        team: 'structure'
                                    },
                                    loadComponent: () =>
                                        import('../pages/document-management-system-import/document-management-system-import').then(
                                            m => m.DocumentManagementSystemImportPage
                                        )
                                },
                                {
                                    path: 'indicators-buildings-comparison/:tab',
                                    data: {
                                        reuse: true,
                                        team: 'ems'
                                    },
                                    loadComponent: () =>
                                        import('../pages/indicators-buildings-comparison/indicators-buildings-comparison').then(
                                            m => m.IndicatorsBuildingsComparisonPage
                                        )
                                },

                                {
                                    path: 'data-health',
                                    data: {
                                        reuse: true
                                    },
                                    loadChildren: () => import('@/features/data-health/data-health.routes').then(m => m.routes)
                                },
                                {
                                    path: 'data-quality-completeness-details',
                                    data: {
                                        team: '???' // to discuss
                                    },
                                    loadComponent: () =>
                                        import('../pages/data-quality-completeness-analysis/data-quality-completeness-details.ng-page').then(
                                            m => m.DataQualityCompletenessDetailsNgPage
                                        )
                                },
                                {
                                    path: 'data-viewer',
                                    data: {
                                        reuse: true
                                    },
                                    loadChildren: () => import('@/features/data-viewer/data-viewer.routes').then(m => m.routes)
                                },
                                {
                                    path: 'invoices/:invoiceId',
                                    data: {
                                        team: 'integration'
                                    },
                                    loadComponent: () =>
                                        import('../pages/bills/invoice-edition/invoice-edition.ng-page').then(m => m.InvoiceEditionNgPage)
                                },
                                {
                                    path: 'specific-manual-index-readings',
                                    loadComponent: () =>
                                        import('../pages/specific-manual-index-readings/specific-manual-index-readings.ng-page').then(
                                            m => m.SpecificManualIndexReadingsNgPage
                                        ),
                                    data: {
                                        team: 'integration'
                                    }
                                },
                                {
                                    path: 'meter-index/:meterId/statement',
                                    loadComponent: () =>
                                        import('../pages/meter/meter-index-statement/meter-index-statement.ng-page').then(
                                            m => m.MeterIndexStatementNgPage
                                        ),
                                    data: {
                                        team: 'integration'
                                    }
                                },
                                {
                                    path: 'bills-import-files',
                                    data: {
                                        team: 'integration'
                                    },
                                    loadComponent: () =>
                                        import('../pages/bills/bills-import-files/bills-import-files.ng-page').then(
                                            m => m.BillsImportFilesNgPage
                                        )
                                },
                                {
                                    path: 'meter-index/:meterId/init',
                                    data: {
                                        team: 'integration'
                                    },
                                    loadComponent: () =>
                                        import('../pages/meter/meter-index-init/meter-index-init.ng-page').then(m => m.MeterIndexInitNgPage)
                                },
                                {
                                    path: 'building-home-dashboard',
                                    data: {
                                        team: 'ems'
                                    },
                                    loadComponent: () =>
                                        import('../pages/desktop/building-home-dashboard/building-home-dashboard.ng-page').then(
                                            m => m.BuildingHomeDashboardNgPage
                                        )
                                },
                                {
                                    path: 'bills/buildings/:dashboardRight/:indicatorCode',
                                    loadComponent: () =>
                                        import('../pages/bills/bills-by-building/bills-by-building.ng-page').then(
                                            m => m.BillsByBuildingNgPage
                                        ),
                                    data: {
                                        team: 'ems'
                                    }
                                },
                                {
                                    path: 'bills/buildings/:buildingId/analysis/:universeId',
                                    loadComponent: () =>
                                        import('../pages/bills/bills-by-building-analysis/bills-by-building-analysis.ng-page').then(
                                            m => m.BillsByBuildingAnalysisNgPage
                                        ),
                                    data: {
                                        team: 'ems'
                                    }
                                },
                                {
                                    path: 'delivery-point/add',
                                    data: {
                                        team: 'structure'
                                    },
                                    loadComponent: () =>
                                        import('../pages/bills/delivery-point-add/delivery-point-add.ng-page').then(
                                            m => m.DeliveryPointAddNgPage
                                        )
                                },
                                {
                                    path: 'connect',
                                    loadComponent: () =>
                                        import('../pages/connected-as/connected-as.ng-page').then(m => m.ConnectedAsNgPage),
                                    data: {
                                        team: 'structure'
                                    }
                                },
                                {
                                    path: 'ipe-history/:tileId',
                                    data: {
                                        team: 'ems'
                                    },
                                    loadComponent: () => import('../pages/ipe-history/ipe-history.ng-page').then(m => m.IpeHistoryNgPage)
                                },
                                {
                                    path: 'supplier-portal-access',
                                    loadComponent: () =>
                                        import('../pages/bills/supplier-portal-access/supplier-portal-access.ng-page').then(
                                            m => m.SupplierPortalAccessNgPage
                                        ),
                                    data: {
                                        team: 'integration'
                                    }
                                },
                                {
                                    path: 'document-management-system-page',
                                    data: {
                                        team: 'structure'
                                    },
                                    loadComponent: () =>
                                        import('../pages/document-management-system/document-management-system.ng-page').then(
                                            m => m.DocumentManagementSystemNgPage
                                        )
                                },
                                {
                                    path: 'contract-power-optimization',
                                    data: {
                                        team: 'integration' // to discuss
                                    },
                                    loadChildren: () =>
                                        import('@/features/contract-power-optimization/contract-power-optimization.routes').then(
                                            m => m.routes
                                        )
                                },
                                {
                                    path: 'suppliers-missing-invoices',
                                    data: {
                                        team: 'integration'
                                    },
                                    loadComponent: () =>
                                        import('../pages/suppliers-missing-invoices/suppliers-missing-invoices.ng-page').then(
                                            m => m.SuppliersMissingInvoicesNgPage
                                        )
                                },
                                {
                                    path: 'budget-edition',
                                    data: {
                                        team: 'ems'
                                    },
                                    loadComponent: () => import('../pages/budget-edition/budget-edition').then(m => m.BudgetEditionPage)
                                },
                                {
                                    path: 'funnel-view/:dashboardRight/:timeseriesIndicator',
                                    loadComponent: () =>
                                        import('../pages/budget-funnel-view/budget-funnel-view.ng-page').then(
                                            m => m.BudgetFunnelViewNgPage
                                        ),
                                    data: {
                                        team: 'ems'
                                    }
                                },
                                {
                                    path: 'tile-creation-menu/:tileGroup',
                                    loadComponent: () =>
                                        import('../pages/tile-creation-menu/tile-creation-menu.ng-page').then(
                                            m => m.TileCreationMenuNgPage
                                        ),
                                    data: {
                                        team: 'ems'
                                    }
                                },
                                {
                                    path: 'tile-creation-multi-sensor/:tileGroup',
                                    loadComponent: () =>
                                        import('../pages/tile-creation-multi-sensor/tile-creation-multi-sensor.ng-page').then(
                                            m => m.TileCreationMultiSensorNgPage
                                        ),
                                    data: {
                                        team: 'ems'
                                    }
                                },
                                {
                                    path: 'invoice',
                                    data: {
                                        team: 'integration'
                                    },
                                    loadComponent: () => import('../pages/bills/bills-home/bills-home.ng-page').then(m => m.BillsHomeNgPage)
                                },
                                {
                                    path: 'invoice/error/:invoiceReference/:supplierId',
                                    data: {
                                        team: 'integration' // to discuss
                                    },
                                    loadComponent: () =>
                                        import('../pages/bills/invoice-error/invoice-error.ng-page').then(m => m.InvoiceErrorNgPage)
                                },
                                {
                                    path: 'invoice-discrepancy',
                                    data: {
                                        reuse: true,
                                        team: 'integration'
                                    },
                                    loadComponent: () =>
                                        import('../pages/invoice-discrepancy/invoice-discrepancy.ng-page').then(
                                            m => m.InvoiceDiscrepancyNgPage
                                        )
                                },
                                {
                                    path: 'no-building-access',
                                    loadComponent: () =>
                                        import('../pages/no-building-access/no-building-access.ng-page').then(
                                            m => m.NoBuildingAccessNgPage
                                        ),
                                    data: {
                                        team: 'structure'
                                    }
                                },
                                {
                                    path: 'test-datasource',
                                    loadComponent: () =>
                                        import('../pages/test-datasource/test-datasource.ng-page').then(m => m.TestDatasourceNgPage)
                                },
                                {
                                    path: 'dashboard-test',
                                    loadComponent: () =>
                                        import('../pages/data-viz/dashboard/dashboard.ng-page').then(m => m.DashboardNgPage)
                                },
                                {
                                    path: 'virtual-system-edition/:buildingUid/:systemUid',
                                    loadComponent: () =>
                                        import('../pages/systems/virtual-system-edition/virtual-system-edition.ng-page').then(
                                            m => m.VirtualSystemEditionNgPage
                                        ),
                                    data: {
                                        team: 'integration'
                                    }
                                },
                                {
                                    path: 'virtual-sensor-edition',
                                    loadComponent: () =>
                                        import('../pages/sensors/virtual-sensor-edition/virtual-sensor-edition.ng-page').then(
                                            m => m.VirtualSensorEditionNgPage
                                        ),
                                    data: {
                                        team: 'integration'
                                    }
                                },
                                {
                                    path: 'readings/add',
                                    data: {
                                        team: 'integration'
                                    },
                                    loadComponent: () =>
                                        import('../pages/systems/system-add/system-add.ng-page').then(m => m.SystemAddNgPage)
                                },
                                {
                                    path: 'virtual-system-add',
                                    data: {
                                        team: 'integration'
                                    },
                                    loadComponent: () =>
                                        import('../pages/systems/virtual-system-add/virtual-system-add.ng-page').then(
                                            m => m.VirtualSystemAddNgPage
                                        )
                                },
                                {
                                    path: 'system-add-advanced',
                                    data: {
                                        team: 'integration'
                                    },
                                    loadComponent: () =>
                                        import('../pages/systems/system-add-advanced/system-add-advanced.ng-page').then(
                                            m => m.SystemAddAdvancedNgPage
                                        )
                                },
                                {
                                    path: 'managed-entity',
                                    data: {
                                        team: 'structure'
                                    },
                                    loadComponent: () =>
                                        import('../pages/managed-entity/managed-entity.ng-page').then(m => m.ManagedEntityNgPage)
                                },
                                {
                                    path: 'deet',
                                    data: {
                                        team: 'ems'
                                    },
                                    loadChildren: () => import('@/features/deet/deet.routes').then(m => m.routes)
                                },
                                {
                                    path: 'mip',
                                    data: {
                                        team: 'ems'
                                    },
                                    loadChildren: () => import('@/features/mip/mip.routes').then(m => m.routes)
                                }
                            ]
                        }
                    ]
                },
                {
                    path: 'account/reset-password/:email/:token',
                    loadComponent: () => import('../pages/confirm-password/confirm-password.ng-page').then(m => m.ConfirmPasswordNgPage),
                    data: {
                        standalone: true
                    }
                },
                {
                    path: '**',
                    redirectTo: ''
                }
            ],
            routerOptions
        )
    ],
    exports: [RouterModule]
})
export class AppRoutingModule {}
